import {
  CheckCircle2,
  ChevronDown,
  CircleHelp,
  Mail,
  Map,
  Play,
  RefreshCw,
  Send,
  Shield,
  SlidersHorizontal,
  Smartphone,
  Trash2,
  UserRoundX
} from "lucide-react";
import { FormEvent, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import routeMapExampleUrl from "../assets/route-map-example.png";
import { api } from "../api/client";
import { track, trackPerformance } from "../api/telemetry";
import { deriveSetupReadiness, JourneyId, SetupReadiness } from "../readiness";
import type {
  ActivityType,
  AspectRatio,
  DeliveryChannel,
  MapImageryType,
  MapProvider,
  OutputMediaType,
  PreferenceSetUpdateRequest,
  PreferenceUpdateRequest,
  PreferencesResponse,
  ProcessingAttemptSummary,
  PublicStatusResponse,
  ServiceNoticeResponse,
  StravaActivitySummary,
  WatermarkPosition
} from "../types";

const activityTypeGroups: Array<{ label: string; options: ActivityType[] }> = [
  { label: "Foot", options: ["Run", "TrailRun", "Hike", "Walk", "Wheelchair"] },
  {
    label: "Cycle",
    options: [
      "Ride",
      "MountainBikeRide",
      "GravelRide",
      "EBikeRide",
      "EMountainBikeRide",
      "Velomobile",
      "Handcycle"
    ]
  },
  {
    label: "Water",
    options: [
      "Canoeing",
      "Kayaking",
      "StandUpPaddling",
      "Kitesurf",
      "Rowing",
      "Sail",
      "Windsurf",
      "Swim",
      "Surfing"
    ]
  },
  {
    label: "Winter",
    options: [
      "AlpineSki",
      "NordicSki",
      "BackcountrySki",
      "Snowshoe",
      "Snowboard",
      "IceSkate",
      "RollerSki"
    ]
  },
  {
    label: "Court and team",
    options: [
      "Badminton",
      "Basketball",
      "Cricket",
      "Golf",
      "Padel",
      "Pickleball",
      "Racquetball",
      "Soccer",
      "Squash",
      "TableTennis",
      "Tennis",
      "Volleyball"
    ]
  },
  {
    label: "Training",
    options: [
      "Crossfit",
      "Dance",
      "Elliptical",
      "HighIntensityIntervalTraining",
      "InlineSkate",
      "PhysicalTherapy",
      "Pilates",
      "RockClimbing",
      "Skateboard",
      "StairStepper",
      "WeightTraining",
      "Workout",
      "Yoga"
    ]
  }
];
const zwiftActivityTypeGroups: Array<{ label: string; options: ActivityType[] }> = [
  { label: "Zwift", options: ["VirtualRide", "VirtualRun"] }
];

const fallbackPreferences: PreferencesResponse = {
  strava_authenticated: false,
  integration_enabled: false,
  aspect_ratio: "square",
  map_provider: "ordnance_survey",
  imagery_type: "default",
  activity_types: ["Run"],
  delivery_channel: "email",
  masked_destination: "",
  verification_status: null,
  privacy_disclosure_accepted: false,
  privacy_policy_accepted: false,
  provider_terms_accepted: false,
  delivery_consent_accepted: false,
  age_eligibility_confirmed: false,
  disclosure_version: "",
  privacy_disclosure_accepted_at: null,
  privacy_policy_accepted_at: null,
  provider_terms_accepted_at: null,
  delivery_consent_accepted_at: null,
  age_eligibility_confirmed_at: null,
  privacy_radius_meters: 400,
  route_colour: "#c5285d",
  route_border_colour: "#7e2044",
  output_media_type: "image",
  animation_marker_colour: "#c5285d",
  watermark_text: "maps.mitch.fitness",
  watermark_position: "bottom_right",
  preference_sets: [
    {
      set_id: "default",
      name: "Default",
      enabled: true,
      is_default: true,
      aspect_ratio: "square",
      map_provider: "ordnance_survey",
      imagery_type: "default",
      activity_types: ["Run"],
      privacy_radius_meters: 400,
      route_colour: "#c5285d",
      route_border_colour: "#7e2044",
      output_media_type: "image",
      animation_marker_colour: "#c5285d",
      watermark_text: "maps.mitch.fitness",
      watermark_position: "bottom_right",
      warnings: []
    }
  ],
  warnings: []
};

const fallbackServiceStatus: PublicStatusResponse = {
  available_delivery_channels: ["email"],
  capacity_full: false,
  processing_available: true,
  service_notices: {
    public: null,
    authenticated: null
  },
  support_email: "maps@example.com"
};

const statusUnavailableNotice: ServiceNoticeResponse = {
  id: "status_unavailable",
  title: "Service status could not be checked",
  message: "Setup can continue, but map availability may be out of date. Try refreshing before starting a new activity test.",
  tone: "warning",
  support_subject: "Service status unavailable"
};

const knownFrontendPaths = new Set(["/", "/faq", "/privacy-security", "/settings/delivery"]);
const journeyHashPrefix = "#tab=";
const validJourneyIds: JourneyId[] = ["setup", "test", "preferences", "account", "help"];
const supportedDeliveryChannels: DeliveryChannel[] = ["email", "telegram", "whatsapp"];

type ActionFeedback = {
  message: string;
  tone: "success" | "warning" | "error" | "info";
};

function deliveryChannelLabel(channel: DeliveryChannel): string {
  if (channel === "email") {
    return "Email";
  }
  if (channel === "telegram") {
    return "Telegram";
  }
  return "WhatsApp";
}

function deliveryDestinationLabel(channel: DeliveryChannel): string {
  if (channel === "email") {
    return "Email address";
  }
  if (channel === "telegram") {
    return "Telegram chat ID";
  }
  return "WhatsApp number";
}

function deliveryDestinationPlaceholder(channel: DeliveryChannel): string {
  if (channel === "email") {
    return "runner@example.com";
  }
  if (channel === "telegram") {
    return "123456789";
  }
  return "+447700900123";
}

function verificationPrompt(channel: DeliveryChannel): string {
  if (channel === "email") {
    return "We sent a verification code to your email address. Check your inbox and junk or spam folder, then enter the code here.";
  }
  if (channel === "telegram") {
    return "The Telegram bot sends a verification code in chat. Open Telegram, find the bot message, then enter the code here.";
  }
  return "We sent a verification code by WhatsApp. Open WhatsApp, find the message, then enter the code here.";
}

function stravaActivityUrl(activityId: string): string {
  return `https://www.strava.com/activities/${encodeURIComponent(activityId)}`;
}

function savedContactLabel(preferences: PreferencesResponse, telegramLinkStarted = false): string {
  if (preferences.delivery_channel === "telegram") {
    if (preferences.verification_status === "verified") {
      return "Telegram chat linked";
    }
    if (preferences.verification_status === "pending" || telegramLinkStarted) {
      return "Telegram chat pending verification";
    }
    return "None";
  }
  return preferences.masked_destination || "None";
}

function deliveryIcon(channel: DeliveryChannel, size = 20): ReactNode {
  if (channel === "email") {
    return <Mail aria-hidden="true" size={size} />;
  }
  if (channel === "telegram") {
    return <Send aria-hidden="true" size={size} />;
  }
  return <Smartphone aria-hidden="true" size={size} />;
}

export function deliveryChannelsWithDefaults(channels?: DeliveryChannel[]): DeliveryChannel[] {
  const available = channels?.filter((channel): channel is DeliveryChannel =>
    supportedDeliveryChannels.includes(channel)
  );
  const unique = [...new Set(available)];
  return unique.length ? unique : fallbackServiceStatus.available_delivery_channels;
}

function deliveryChannelOrDefault(channel: DeliveryChannel, availableChannels: DeliveryChannel[]): DeliveryChannel {
  return availableChannels.includes(channel) ? channel : (availableChannels[0] ?? "email");
}

function activityLabel(activity: StravaActivitySummary) {
  const date = new Date(activity.start_date);
  const dateLabel = Number.isNaN(date.valueOf()) ? "" : date.toLocaleDateString();
  const distanceKm = (activity.distance_meters / 1000).toFixed(1);
  return `${activity.name} - ${activity.activity_type} - ${distanceKm} km${dateLabel ? ` - ${dateLabel}` : ""}`;
}

function activityTypeLabel(activityType: ActivityType) {
  const labels: Record<ActivityType, string> = {
    AlpineSki: "Alpine ski",
    BackcountrySki: "Backcountry ski",
    Badminton: "Badminton",
    Basketball: "Basketball",
    Canoeing: "Canoe",
    Cricket: "Cricket",
    Crossfit: "CrossFit",
    Dance: "Dance",
    EBikeRide: "E-bike ride",
    Elliptical: "Elliptical",
    EMountainBikeRide: "E-mountain bike ride",
    Golf: "Golf",
    GravelRide: "Gravel ride",
    Handcycle: "Handcycle",
    HighIntensityIntervalTraining: "HIIT",
    Run: "Run",
    TrailRun: "Trail run",
    Hike: "Hike",
    IceSkate: "Ice skate",
    InlineSkate: "Inline skate",
    Kayaking: "Kayak",
    Kitesurf: "Kitesurf",
    MountainBikeRide: "Mountain bike ride",
    NordicSki: "Nordic ski",
    Padel: "Padel",
    PhysicalTherapy: "Physical therapy",
    Pickleball: "Pickleball",
    Pilates: "Pilates",
    Racquetball: "Racquetball",
    RockClimbing: "Rock climbing",
    RollerSki: "Roller ski",
    Ride: "Ride",
    Rowing: "Rowing",
    Sail: "Sailing",
    Skateboard: "Skateboard",
    Snowboard: "Snowboard",
    Snowshoe: "Snowshoe",
    Soccer: "Soccer",
    Squash: "Squash",
    StairStepper: "Stair stepper",
    StandUpPaddling: "SUP",
    Surfing: "Surf",
    Swim: "Swim",
    TableTennis: "Table tennis",
    Tennis: "Tennis",
    Velomobile: "Velomobile",
    Volleyball: "Volleyball",
    Walk: "Walk",
    WeightTraining: "Weight training",
    Wheelchair: "Wheelchair",
    Windsurf: "Windsurf",
    Workout: "Workout",
    Yoga: "Yoga",
    VirtualRide: "Virtual ride",
    VirtualRun: "Virtual run"
  };
  return labels[activityType];
}

function activityGroupsForProvider(mapProvider: MapProvider) {
  return mapProvider === "zwift" ? zwiftActivityTypeGroups : activityTypeGroups;
}

function imageryOptionsForProvider(mapProvider: MapProvider) {
  if (mapProvider === "zwift") {
    return [{ value: "zwift_world" as MapImageryType, label: "Zwift world" }];
  }
  return [
    { value: "default" as MapImageryType, label: "Standard Ordnance Survey" },
    { value: "mastermap" as MapImageryType, label: "MasterMap" },
    { value: "explorer_25000" as MapImageryType, label: "Explorer 1:25000" },
    { value: "landranger_50000" as MapImageryType, label: "Landranger 1:50000" }
  ];
}

function defaultPreferenceSet(preferences: PreferencesResponse): PreferenceSetUpdateRequest {
  return {
    set_id: "default",
    name: "Default",
    enabled: true,
    is_default: true,
    aspect_ratio: preferences.aspect_ratio,
    map_provider: preferences.map_provider,
    imagery_type: preferences.imagery_type,
    activity_types: preferences.activity_types.length ? preferences.activity_types : ["Run"],
    privacy_radius_meters: preferences.privacy_radius_meters,
    route_colour: preferences.route_colour,
    route_border_colour: preferences.route_border_colour,
    output_media_type: preferences.output_media_type,
    animation_marker_colour: preferences.animation_marker_colour,
    watermark_text: preferences.watermark_text,
    watermark_position: preferences.watermark_position
  };
}

function preferenceRequest(
  preferences: PreferencesResponse,
  overrides: Partial<PreferenceUpdateRequest> = {}
): PreferenceUpdateRequest {
  const preferenceSets = preferences.preference_sets.length
    ? preferences.preference_sets.map((preferenceSet) => ({
        set_id: preferenceSet.set_id,
        name: preferenceSet.name,
        enabled: preferenceSet.enabled,
        is_default: preferenceSet.is_default,
        aspect_ratio: preferenceSet.aspect_ratio,
        map_provider: preferenceSet.map_provider,
        imagery_type: preferenceSet.imagery_type,
        activity_types: preferenceSet.activity_types,
        privacy_radius_meters: preferenceSet.privacy_radius_meters,
        route_colour: preferenceSet.route_colour,
        route_border_colour: preferenceSet.route_border_colour,
        output_media_type: preferenceSet.output_media_type,
        animation_marker_colour: preferenceSet.animation_marker_colour,
        watermark_text: preferenceSet.watermark_text,
        watermark_position: preferenceSet.watermark_position
      }))
    : [defaultPreferenceSet(preferences)];
  const selectedSet = preferenceSets.find((preferenceSet) => preferenceSet.is_default) ?? preferenceSets[0];
  return {
    integration_enabled: preferences.integration_enabled,
    aspect_ratio: selectedSet.aspect_ratio,
    map_provider: selectedSet.map_provider,
    imagery_type: selectedSet.imagery_type,
    activity_types: selectedSet.activity_types.length ? selectedSet.activity_types : ["Run"],
    privacy_radius_meters: selectedSet.privacy_radius_meters,
    delivery_channel: preferences.delivery_channel,
    route_colour: selectedSet.route_colour,
    route_border_colour: selectedSet.route_border_colour,
    output_media_type: selectedSet.output_media_type,
    animation_marker_colour: selectedSet.animation_marker_colour,
    watermark_text: selectedSet.watermark_text,
    watermark_position: selectedSet.watermark_position,
    preference_sets: preferenceSets,
    ...overrides
  };
}

export function selectedPreferenceSetId(
  preferenceSets: Pick<PreferenceSetUpdateRequest, "set_id" | "is_default">[],
  currentSetId?: string
): string {
  if (currentSetId && preferenceSets.some((preferenceSet) => preferenceSet.set_id === currentSetId)) {
    return currentSetId;
  }
  return (
    preferenceSets.find((preferenceSet) => preferenceSet.is_default)?.set_id ??
    preferenceSets[0]?.set_id ??
    "default"
  );
}

function readinessLabel(readiness: SetupReadiness) {
  if (readiness.readyForAutomaticProcessing()) {
    return "Ready to create maps automatically";
  }
  if (readiness.readyForManualTesting()) {
    return "Ready to test an activity";
  }
  return "Finish setup to start processing";
}

export function readinessContactLabel(readiness: SetupReadiness) {
  if (readiness.contactUsable) {
    return "Contact verified";
  }
  const contactBlocker = readiness.blockers.find(
    (blocker) => blocker.code === "contact_missing" || blocker.code === "contact_not_verified"
  );
  return contactBlocker?.code === "contact_not_verified" ? "Contact pending" : "Contact needed";
}

function loadedSetupStatus(readiness: SetupReadiness) {
  if (readiness.readyForAutomaticProcessing()) {
    return "Auto-processing is on for new activities that match your map settings.";
  }
  if (readiness.readyForManualTesting()) {
    return "Run a manual test, then turn on auto-processing when the output looks right.";
  }
  return "Continue the setup steps below to start creating map images.";
}

export function isKnownFrontendPath(pathname: string) {
  return knownFrontendPaths.has(pathname);
}

export function journeyFromHash(hash: string): JourneyId | null {
  if (!hash.startsWith(journeyHashPrefix)) {
    return null;
  }
  const candidate = hash.slice(journeyHashPrefix.length);
  return validJourneyIds.includes(candidate as JourneyId) ? (candidate as JourneyId) : null;
}

export function publicStatusWithDefaults(
  status: Partial<PublicStatusResponse> = {}
): PublicStatusResponse {
  return {
    ...fallbackServiceStatus,
    ...status,
    capacity_full: Boolean(status.capacity_full),
    available_delivery_channels: deliveryChannelsWithDefaults(status.available_delivery_channels),
    processing_available: status.processing_available ?? fallbackServiceStatus.processing_available,
    service_notices: {
      ...fallbackServiceStatus.service_notices,
      ...(status.service_notices ?? {})
    },
    support_email: status.support_email || fallbackServiceStatus.support_email
  };
}

function statusUnavailableFallback(): PublicStatusResponse {
  return publicStatusWithDefaults({
    service_notices: {
      public: statusUnavailableNotice,
      authenticated: statusUnavailableNotice
    }
  });
}

export function App() {
  const pathname = window.location.pathname;
  if (pathname === "/privacy-security") {
    return <PrivacySecurityPolicyPage />;
  }
  if (pathname === "/faq") {
    return <FaqPage />;
  }
  if (!isKnownFrontendPath(pathname)) {
    return <NotFoundPage pathname={pathname} />;
  }
  return <ConfigurationApp />;
}

function ConfigurationApp() {
  const [preferences, setPreferences] = useState<PreferencesResponse>(fallbackPreferences);
  const [status, setStatus] = useState("Checking your Strava connection...");
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const [activeJourney, setActiveJourney] = useState<JourneyId>(() => journeyFromHash(window.location.hash) ?? "setup");
  const [capacityFull, setCapacityFull] = useState(false);
  const [serviceStatus, setServiceStatus] = useState<PublicStatusResponse>(fallbackServiceStatus);
  const journeyRegionRef = useRef<HTMLElement>(null);
  const readiness = useMemo(() => deriveSetupReadiness(preferences), [preferences]);

  useEffect(() => {
    void refreshPreferences();
  }, []);

  useEffect(() => {
    if (preferences.strava_authenticated && readiness.readyForAutomaticProcessing()) {
      setActiveJourney((current) => (current === "setup" ? "test" : current));
    }
  }, [preferences.strava_authenticated, readiness]);

  useEffect(() => {
    if (!preferencesLoaded) {
      return;
    }
    if (!preferences.strava_authenticated) {
      if (window.location.hash) {
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
      }
      return;
    }
    const nextHash = `${journeyHashPrefix}${activeJourney}`;
    if (window.location.hash !== nextHash) {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${nextHash}`);
    }
  }, [activeJourney, preferences.strava_authenticated, preferencesLoaded]);

  async function refreshPreferences() {
    const started = performance.now();
    try {
      const publicStatus = await api.getPublicStatus();
      const normalizedStatus = publicStatusWithDefaults(publicStatus);
      setServiceStatus(normalizedStatus);
      setCapacityFull(normalizedStatus.capacity_full);
    } catch {
      setServiceStatus(statusUnavailableFallback());
      setCapacityFull(false);
      void track("client_error", { page: "configuration", reason_code: "public_status_failed" });
    }
    try {
      const response = await api.getPreferences();
      setPreferences(response);
      if (response.strava_authenticated) {
        setActiveJourney((current) => journeyFromHash(window.location.hash) ?? current);
      }
      setStatus(loadedSetupStatus(deriveSetupReadiness(response)));
      setPreferencesLoaded(true);
      void track("app_loaded", { page: "configuration", status: "api_loaded" });
      void trackPerformance("configuration_load_ms", performance.now() - started);
    } catch {
      setStatus("We could not check your saved setup. You can still try connecting Strava.");
      setPreferencesLoaded(true);
      void track("client_error", { page: "configuration", reason_code: "preferences_load_failed" });
    }
  }

  function replacePreferences(response: PreferencesResponse, nextStatus: string) {
    setPreferences(response);
    setStatus(nextStatus);
  }

  function selectJourney(journey: JourneyId) {
    setActiveJourney(journey);
    window.requestAnimationFrame(() => {
      journeyRegionRef.current?.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
        block: "start"
      });
    });
  }

  async function linkStrava(page: string) {
    setStatus("Sending you to Strava...");
    setCapacityFull(false);
    void track("strava_link_clicked", { page });
    try {
      const response = await api.startStravaAuthorization();
      window.location.assign(response.authorization_url);
    } catch (error) {
      const message = error instanceof Error ? error.message : "";
      const isCapacityFull = message.toLowerCase().includes("capacity");
      setCapacityFull(isCapacityFull);
      setStatus(isCapacityFull ? "We are at capacity right now. Join the waitlist for access." : "Could not start Strava connection.");
      void track("client_error", { page, reason_code: "strava_link_start_failed" });
    }
  }

  async function joinWaitlist(emailAddress: string) {
    await api.joinWaitlist(emailAddress);
    setStatus("You are on the waitlist.");
    void track("waitlist_submitted", { status: "submitted" });
  }

  if (!preferencesLoaded || !preferences.strava_authenticated) {
    return (
      <WelcomeView
        capacityFull={capacityFull}
        checkingSetup={!preferencesLoaded}
        serviceStatus={serviceStatus}
        status={status}
        onJoinWaitlist={joinWaitlist}
        onLinkStrava={() => void linkStrava("welcome")}
      />
    );
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <div>
          <h1>Custom activity maps</h1>
          <p>{status}</p>
        </div>
        <button type="button" className="secondary-action" onClick={() => void linkStrava("configuration")}>
          Reconnect Strava
        </button>
      </header>

      <DashboardShell
        activeJourney={activeJourney}
        readiness={readiness}
        serviceStatus={serviceStatus}
        status={status}
        onSelectJourney={selectJourney}
      />

      <section className="journey-region" ref={journeyRegionRef}>
        {activeJourney === "setup" ? (
          <SetupJourney
            preferences={preferences}
            readiness={readiness}
            replacePreferences={replacePreferences}
            serviceStatus={serviceStatus}
          />
        ) : null}
        {activeJourney === "test" ? (
          <ManualTestJourney
            preferences={preferences}
            readiness={readiness}
            serviceStatus={serviceStatus}
          />
        ) : null}
        {activeJourney === "preferences" ? (
          <PreferencesJourney preferences={preferences} replacePreferences={replacePreferences} />
        ) : null}
        {activeJourney === "account" ? (
          <AccountManagementJourney
            preferences={preferences}
            replacePreferences={replacePreferences}
            onUnlinked={() => {
              setPreferences(fallbackPreferences);
              setActiveJourney("setup");
              setStatus("Strava account unlinked");
            }}
          />
        ) : null}
        {activeJourney === "help" ? <HelpJourney serviceStatus={serviceStatus} /> : null}
      </section>
    </main>
  );
}

function NotFoundPage({ pathname }: { pathname: string }) {
  return (
    <main className="app-shell">
      <header className="topbar">
        <div>
          <h1>Custom activity maps</h1>
          <p>That page does not exist.</p>
        </div>
        <a className="button-link" href="/">
          Go to setup
        </a>
      </header>
      <section className="journey-region">
        <section className="task-panel">
          <p className="eyebrow">Not found</p>
          <h2>We could not find {pathname}</h2>
          <p className="muted">
            The map setup, account controls, and privacy notice are still available.
          </p>
          <div className="button-row">
            <a className="button-link" href="/">
              Open setup
            </a>
            <a className="button-link" href="/privacy-security">
              Privacy and security
            </a>
          </div>
        </section>
      </section>
    </main>
  );
}

interface WelcomeViewProps {
  capacityFull: boolean;
  checkingSetup: boolean;
  serviceStatus: PublicStatusResponse;
  status: string;
  onJoinWaitlist(emailAddress: string): Promise<void>;
  onLinkStrava(): void;
}

function WelcomeView({
  capacityFull,
  checkingSetup,
  serviceStatus,
  status,
  onJoinWaitlist,
  onLinkStrava
}: WelcomeViewProps) {
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const welcomeSubtitle = checkingSetup
    ? "Checking whether Strava is already connected..."
    : capacityFull
      ? "We are at capacity right now. Join the waitlist for access."
      : !serviceStatus.processing_available
        ? "Map generation is temporarily paused, but setup and account controls remain available."
        : status;

  async function submitWaitlist(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await onJoinWaitlist(waitlistEmail);
    setWaitlistEmail("");
  }

  return (
    <main className="app-shell welcome-shell" aria-busy={checkingSetup}>
      <header className="topbar">
        <div>
          <h1>Custom activity maps</h1>
          <p>{welcomeSubtitle}</p>
        </div>
        <nav className="topbar-actions" aria-label="Public pages">
          <a className="button-link secondary-action" href="/faq">
            FAQ
          </a>
          <a className="button-link secondary-action" href="/privacy-security">
            Privacy
          </a>
          {checkingSetup ? (
            <button type="button" className="primary-action" disabled>
              Checking setup...
            </button>
          ) : capacityFull ? (
            <a className="button-link hero-link" href="#waitlist">
              Join the waitlist
            </a>
          ) : (
            <button type="button" className="primary-action" onClick={onLinkStrava}>
              Connect Strava
            </button>
          )}
        </nav>
      </header>
      <ServiceNoticeBanner
        notice={serviceStatus.service_notices.public}
        supportEmail={serviceStatus.support_email}
      />

      <section className="welcome-hero" style={{ backgroundImage: `url(${routeMapExampleUrl})` }}>
        <div className="welcome-hero-content">
          <p className="eyebrow">Custom route maps and animations from Strava</p>
          <h2>Turn your activities into shareable map media</h2>
          <p>
            {checkingSetup
              ? "One moment while we check whether you already have a Strava connection. Setup options will appear as soon as that check finishes."
              : capacityFull
                ? "Access is currently limited while the service stays within its operating cap. Join the waitlist and we will let you know when a place opens."
                : "Connect Strava once, choose your map, privacy, and output settings, then receive a still image or route animation after each eligible outdoor or Zwift activity."}
          </p>
          {checkingSetup ? (
            <div className="button-row">
              <button type="button" className="primary-action" disabled>
                Checking Strava connection...
              </button>
              <a className="button-link hero-link" href="#what-you-get">
                See the output
              </a>
              <a className="button-link hero-link" href="/faq">
                Read the FAQ
              </a>
            </div>
          ) : capacityFull ? (
            <WaitlistForm
              emailAddress={waitlistEmail}
              onEmailChange={setWaitlistEmail}
              onSubmit={submitWaitlist}
            />
          ) : (
            <div className="button-row">
              <button type="button" className="primary-action" onClick={onLinkStrava}>
                Connect Strava
              </button>
              <a className="button-link hero-link" href="#what-you-get">
                See the output
              </a>
              <a className="button-link hero-link" href="/faq">
                Read the FAQ
              </a>
            </div>
          )}
        </div>
        <p className="map-credit">
          Contains Ordnance Survey data (c) Crown copyright and database rights 2026
        </p>
      </section>

      <section className="landing-process" aria-labelledby="how-it-works">
        <div className="landing-section-heading">
          <p className="eyebrow">How it works</p>
          <h2 id="how-it-works">From activity to finished map media</h2>
        </div>
        <div className="process-grid">
          <article className="process-step">
            <Shield aria-hidden="true" size={22} />
            <h3>Connect Strava</h3>
            <p>Authorize access to outdoor and virtual activities, then set the privacy radius for route endpoints.</p>
          </article>
          <article className="process-step">
            <SlidersHorizontal aria-hidden="true" size={22} />
            <h3>Choose the output</h3>
            <p>Pick Ordnance Survey or supported Zwift imagery, image or animation output, activity types, styling, and delivery.</p>
          </article>
          <article className="process-step">
            <Send aria-hidden="true" size={22} />
            <h3>Get the result</h3>
            <p>Receive the finished image or animation automatically, or manually test any recent Strava activity.</p>
          </article>
        </div>
      </section>

      <section className="landing-output" id="what-you-get" aria-labelledby="what-you-get-heading">
        <div className="landing-section-heading">
          <p className="eyebrow">What you get</p>
          <h2 id="what-you-get-heading">A route map or animation ready to save, share, or print</h2>
          <p>
            Still images include your route over detailed <abbr title="Ordnance Survey">OS</abbr>
            mapping or supported Zwift world imagery, attribution, and your selected watermark.
            Animations show progress through the route. Privacy-aware trimming helps avoid
            exposing precise start and end locations.
          </p>
        </div>
        <figure className="map-example">
          <img
            src={routeMapExampleUrl}
            alt="Example generated Ordnance Survey-style route map with a highlighted Strava activity route"
            width="1200"
            height="1200"
            loading="lazy"
            decoding="async"
          />
          <figcaption>
            Contains Ordnance Survey data (c) Crown copyright and database rights 2026.
          </figcaption>
        </figure>
      </section>
    </main>
  );
}

function WaitlistForm({
  emailAddress,
  onEmailChange,
  onSubmit
}: {
  emailAddress: string;
  onEmailChange(emailAddress: string): void;
  onSubmit(event: FormEvent<HTMLFormElement>): void;
}) {
  return (
    <form className="capacity-panel" id="waitlist" onSubmit={onSubmit}>
      <label>
        <span>Email address</span>
        <input
          type="email"
          value={emailAddress}
          onChange={(event) => onEmailChange(event.target.value)}
        />
      </label>
      <button type="submit" disabled={!emailAddress.trim()}>
        Join the waitlist
      </button>
    </form>
  );
}

interface DashboardShellProps {
  activeJourney: JourneyId;
  readiness: SetupReadiness;
  serviceStatus: PublicStatusResponse;
  status: string;
  onSelectJourney(journey: JourneyId): void;
}

function DashboardShell({ activeJourney, readiness, serviceStatus, status, onSelectJourney }: DashboardShellProps) {
  const journeys: Array<{ id: JourneyId; label: string; icon: ReactNode }> = [
    { id: "setup", label: "Setup", icon: <Shield aria-hidden="true" size={18} /> },
    { id: "test", label: "Test activity", icon: <Play aria-hidden="true" size={18} /> },
    {
      id: "preferences",
      label: "Map settings",
      icon: <SlidersHorizontal aria-hidden="true" size={18} />
    },
    { id: "account", label: "Account and data", icon: <UserRoundX aria-hidden="true" size={18} /> },
    { id: "help", label: "Help", icon: <CircleHelp aria-hidden="true" size={18} /> }
  ];

  return (
    <section className="dashboard-shell">
      <ReadinessSummary
        readiness={readiness}
        serviceStatus={serviceStatus}
        status={status}
        onPrimaryAction={onSelectJourney}
      />
      <ServiceNoticeBanner
        notice={serviceStatus.service_notices.authenticated}
        supportEmail={serviceStatus.support_email}
      />
      <nav className="journey-tabs" aria-label="Setup tasks">
        {journeys.map((journey) => (
          <button
            key={journey.id}
            type="button"
            className={journey.id === activeJourney ? "tab-button active" : "tab-button"}
            aria-current={journey.id === activeJourney ? "page" : undefined}
            onClick={() => onSelectJourney(journey.id)}
          >
            {journey.icon}
            {journey.label}
          </button>
        ))}
      </nav>
    </section>
  );
}

interface ReadinessSummaryProps {
  readiness: SetupReadiness;
  serviceStatus: PublicStatusResponse;
  status: string;
  onPrimaryAction(journey: JourneyId): void;
}

function ReadinessSummary({ readiness, serviceStatus, status, onPrimaryAction }: ReadinessSummaryProps) {
  const firstTarget = readiness.primaryAction === "test" ? "test" : "setup";
  const primaryActionDisabled = !readiness.blockers[0] && !serviceStatus.processing_available;

  return (
    <section className="readiness-summary" aria-label="Setup status">
      <div>
        <p className="eyebrow">Setup status</p>
        <h2>{readinessLabel(readiness)}</h2>
        <p className="muted">{status}</p>
      </div>
      <div className="readiness-checks">
        <StatusPill ready={readiness.linked}>Strava connected</StatusPill>
        <StatusPill ready={readiness.disclosuresAccepted}>Terms accepted</StatusPill>
        <StatusPill ready={readiness.contactUsable}>{readinessContactLabel(readiness)}</StatusPill>
        <StatusPill ready={readiness.automaticEnabled}>
          {readiness.automaticEnabled ? "Auto-processing on" : "Auto-processing off"}
        </StatusPill>
        <StatusPill ready={serviceStatus.processing_available}>Map generation available</StatusPill>
      </div>
      <div className="summary-action">
        {readiness.blockers[0] ? (
          <p>{readiness.blockers[0].label}</p>
        ) : serviceStatus.processing_available ? (
          <p>Test an activity to check your map output.</p>
        ) : (
          <p>Map generation is paused. Setup and settings remain available.</p>
        )}
        <button type="button" disabled={primaryActionDisabled} onClick={() => onPrimaryAction(firstTarget)}>
          {readiness.blockers[0] ? "Continue setup" : "Test an activity"}
        </button>
      </div>
    </section>
  );
}

function ServiceNoticeBanner({
  notice,
  supportEmail
}: {
  notice: ServiceNoticeResponse | null;
  supportEmail: string;
}) {
  if (!notice) {
    return null;
  }
  return (
    <section className={`service-notice ${notice.tone}`} aria-label="Service status">
      <div>
        <p className="eyebrow">Service status</p>
        <h2>{notice.title}</h2>
        <p>{notice.message}</p>
      </div>
      <a className="button-link" href={supportMailto(supportEmail, notice.support_subject)}>
        <Mail aria-hidden="true" size={18} /> Contact support
      </a>
    </section>
  );
}

function StatusPill({ children, ready }: { children: ReactNode; ready: boolean }) {
  return (
    <span className={ready ? "status-pill ready" : "status-pill pending"}>
      {ready ? <CheckCircle2 aria-hidden="true" size={16} /> : <span className="status-dot" />}
      <span className="sr-only">{ready ? "Ready: " : "Not ready: "}</span>
      {children}
    </span>
  );
}

function ActionFeedbackMessage({ feedback }: { feedback: ActionFeedback | null }) {
  if (!feedback) {
    return null;
  }
  return (
    <p className={`action-feedback ${feedback.tone}`} role="status" aria-live="polite">
      {feedback.tone === "success" ? <CheckCircle2 aria-hidden="true" size={16} /> : <span className="status-dot" />}
      {feedback.message}
    </p>
  );
}

interface JourneyProps {
  preferences: PreferencesResponse;
  replacePreferences(response: PreferencesResponse, status: string): void;
}

interface SetupJourneyProps extends JourneyProps {
  readiness: SetupReadiness;
  serviceStatus: PublicStatusResponse;
}

function SetupJourney({ preferences, readiness, replacePreferences, serviceStatus }: SetupJourneyProps) {
  const availableDeliveryChannels = serviceStatus.available_delivery_channels;
  const availableDeliveryChannelsKey = availableDeliveryChannels.join("|");
  const [deliveryChannel, setDeliveryChannel] = useState<DeliveryChannel>(
    deliveryChannelOrDefault(preferences.delivery_channel, availableDeliveryChannels)
  );
  const [contactDestination, setContactDestination] = useState("");
  const [telegramStartUrl, setTelegramStartUrl] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [ageEligibilityConfirmed, setAgeEligibilityConfirmed] = useState(preferences.age_eligibility_confirmed);
  const [feedback, setFeedback] = useState<ActionFeedback | null>(null);
  const deliveryChannelSaved = deliveryChannel === preferences.delivery_channel;
  const selectedVerificationStatus = deliveryChannelSaved ? preferences.verification_status : null;
  const selectedMaskedDestination = deliveryChannelSaved ? preferences.masked_destination : "";
  const contactIcon = deliveryIcon(deliveryChannel);
  const deliveryLabel = deliveryChannelLabel(deliveryChannel).toLowerCase();
  const telegramLinkStarted = deliveryChannel === "telegram" && Boolean(telegramStartUrl);
  const showVerificationForm = selectedVerificationStatus === "pending" || telegramLinkStarted;
  const telegramContactVerified =
    deliveryChannel === "telegram" && deliveryChannelSaved && selectedVerificationStatus === "verified";
  const canSaveContact =
    deliveryChannel === "telegram"
      ? !telegramContactVerified
      : !deliveryChannelSaved || Boolean(contactDestination.trim());
  const autoProcessingCopy = preferences.integration_enabled
    ? "New eligible Strava activities are being processed automatically."
    : readiness.readyForManualTesting()
      ? "Create and send maps automatically when new eligible Strava activities appear."
      : "Accept the terms and verify your delivery contact before turning this on.";

  useEffect(() => {
    setDeliveryChannel(deliveryChannelOrDefault(preferences.delivery_channel, availableDeliveryChannels));
    setContactDestination("");
    setTelegramStartUrl("");
    setVerificationCode("");
    setAgeEligibilityConfirmed(preferences.age_eligibility_confirmed);
  }, [preferences.delivery_channel, preferences.age_eligibility_confirmed, availableDeliveryChannelsKey]);

  async function acceptPrivacy() {
    try {
      const response = await api.acceptPrivacyDisclosure(ageEligibilityConfirmed);
      replacePreferences(response, "Privacy, map, and delivery terms accepted");
      setFeedback({ tone: "success", message: "Terms accepted. Continue by adding a delivery contact." });
      void track("privacy_disclosure_accepted", { status: "accepted" });
    } catch {
      setFeedback({ tone: "error", message: "Could not save your consent. Try again." });
    }
  }

  async function saveContact(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void track("contact_save_started", { delivery_channel: deliveryChannel });
    try {
      const updatedPreferences = await api.updatePreferences(
        preferenceRequest(preferences, {
          delivery_channel: deliveryChannel,
          integration_enabled: false
        })
      );
      replacePreferences(updatedPreferences, "Delivery method saved. Verify a contact before processing.");
      if (deliveryChannel === "telegram") {
        if (updatedPreferences.verification_status === "verified") {
          setTelegramStartUrl("");
          setVerificationCode("");
          setFeedback({
            tone: "success",
            message: "Telegram delivery method saved. You can now test an activity."
          });
          void track("contact_save_succeeded", { delivery_channel: deliveryChannel });
          return;
        }
        const link = await api.startTelegramLink();
        setTelegramStartUrl(link.start_url);
        setVerificationCode("");
        setFeedback({
          tone: "success",
          message: `Telegram link started. Open ${link.bot_username} and enter the code it sends here.`
        });
        window.open(link.start_url, "_blank", "noopener,noreferrer");
        void track("contact_save_succeeded", { delivery_channel: deliveryChannel });
        return;
      }
      if (!contactDestination.trim()) {
        setFeedback({
          tone: updatedPreferences.verification_status === "verified" ? "success" : "info",
          message:
            updatedPreferences.verification_status === "verified"
              ? `${deliveryChannelLabel(deliveryChannel)} delivery method saved. You can now test an activity.`
              : `${deliveryChannelLabel(deliveryChannel)} delivery method saved. Add and verify a contact before processing.`
        });
        void track("contact_save_succeeded", { delivery_channel: deliveryChannel });
        return;
      }
      const response = await api.updateContact(deliveryChannel, contactDestination);
      replacePreferences(response, `Contact saved. Check ${deliveryLabel} for the verification code.`);
      setFeedback({
        tone: "success",
        message: `Contact saved. Check ${deliveryLabel} for the verification code.`
      });
      setContactDestination("");
      setVerificationCode("");
      void track("contact_save_succeeded", { delivery_channel: deliveryChannel });
    } catch {
      setFeedback({ tone: "error", message: "Could not save that contact. Check it and try again." });
      void track("contact_save_failed", { delivery_channel: deliveryChannel });
    }
  }

  async function verifyContact(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      const response = await api.confirmContact({ code: verificationCode });
      replacePreferences(response, "Delivery contact verified");
      setFeedback({ tone: "success", message: "Delivery contact verified. You can now test an activity." });
      setVerificationCode("");
      void track("contact_verification_succeeded", { delivery_channel: deliveryChannel });
    } catch {
      setFeedback({
        tone: "error",
        message: `That verification code did not work. Check ${deliveryLabel} and try again.`
      });
      void track("contact_verification_failed", { delivery_channel: deliveryChannel });
    }
  }

  async function setAutomaticProcessing(enabled: boolean) {
    try {
      const response = await api.updatePreferences(preferenceRequest(preferences, { integration_enabled: enabled }));
      replacePreferences(response, enabled ? "Auto-processing is on" : "Auto-processing is off");
      setFeedback({
        tone: "success",
        message: enabled ? "Auto-processing is on for new eligible activities." : "Auto-processing is off."
      });
    } catch {
      setFeedback({ tone: "error", message: "Could not update auto-processing. Try again." });
    }
  }

  return (
    <div className="journey-stack">
      <ActionFeedbackMessage feedback={feedback} />

      <section className="task-panel">
        <h2>
          <Shield aria-hidden="true" size={20} /> Privacy, maps, and delivery
        </h2>
        <p>
          We use your Strava route data to create map images and send them to your verified
          delivery contact. Before processing starts, review the full notice and agree to the
          privacy, security, map, and delivery terms.
        </p>
        <p className="muted">
          <a href="/privacy-security" target="_blank" rel="noreferrer">
            Open the Privacy and Security Notice
          </a>
        </p>
        <details>
          <summary>
            <ChevronDown aria-hidden="true" size={18} /> What this means
          </summary>
          <p className="muted">
            We access Strava activity and route data, apply your privacy radius before rendering,
            use Ordnance Survey map imagery, and deliver generated images by your chosen channel.
            Delivery contact details and Strava tokens are encrypted, and generated images are
            treated as short-lived delivery files rather than bulk map downloads.
          </p>
        </details>
        <label className="check-row">
          <input
            checked={ageEligibilityConfirmed}
            disabled={readiness.disclosuresAccepted}
            type="checkbox"
            onChange={(event) => setAgeEligibilityConfirmed(event.target.checked)}
          />
          <span>I am 18 or older, or I have permission from my parent or legal guardian.</span>
        </label>
        <button
          type="button"
          disabled={readiness.disclosuresAccepted || !ageEligibilityConfirmed}
          onClick={() => void acceptPrivacy()}
        >
          {readiness.disclosuresAccepted ? "Notice accepted" : "I agree to the notice"}
        </button>
      </section>

      <form className="task-panel" onSubmit={saveContact}>
        <h2>
          {contactIcon} Where to send maps
        </h2>
        <div className="split-fields">
          <label>
            <span>Delivery method</span>
            <select
              value={deliveryChannel}
              onChange={(event) => {
                setDeliveryChannel(event.target.value as DeliveryChannel);
                setTelegramStartUrl("");
                setVerificationCode("");
                setFeedback({ tone: "info", message: "Delivery method changed. Save and verify a contact before processing." });
              }}
            >
              {availableDeliveryChannels.map((channel) => (
                <option key={channel} value={channel}>
                  {deliveryChannelLabel(channel)}
                </option>
              ))}
            </select>
          </label>
          {deliveryChannel !== "telegram" ? (
            <label>
              <span>{deliveryDestinationLabel(deliveryChannel)}</span>
              <input
                value={contactDestination}
                placeholder={deliveryDestinationPlaceholder(deliveryChannel)}
                onChange={(event) => setContactDestination(event.target.value)}
              />
            </label>
          ) : null}
        </div>
        {deliveryChannel === "telegram" ? (
          <>
            <p className="muted">
              Telegram delivery starts by opening a chat with @CustomActivityMapsBot. The bot links
              the chat with this setup session and sends a verification code before generated map
              images are delivered.
            </p>
            {telegramStartUrl ? (
              <p className="muted">
                <a href={telegramStartUrl} target="_blank" rel="noreferrer">
                  Open @CustomActivityMapsBot
                </a>
              </p>
            ) : null}
          </>
        ) : null}
        {deliveryChannel === "whatsapp" ? (
          <p className="muted">
            WhatsApp delivery means agreeing to receive generated activity-map messages at the
            number you provide. You can withdraw this by deleting your contact, changing delivery
            channel, revoking consent, or unlinking Strava.
          </p>
        ) : null}
        <dl className="compact-list">
          <dt>Saved contact</dt>
          <dd>
            {savedContactLabel(
              {
                ...preferences,
                delivery_channel: deliveryChannel,
                masked_destination: selectedMaskedDestination,
                verification_status: selectedVerificationStatus
              },
              telegramLinkStarted
            )}
          </dd>
        </dl>
        {telegramContactVerified ? (
          <p className="action-feedback success" role="status">
            <CheckCircle2 aria-hidden="true" size={16} /> Telegram chat verified
          </p>
        ) : (
          <button type="submit" disabled={!canSaveContact}>
            {deliveryChannel === "telegram"
              ? deliveryChannelSaved
                ? "Start Telegram link"
                : "Save delivery method"
              : deliveryChannelSaved
                ? "Save delivery contact"
                : "Save delivery method"}
          </button>
        )}
      </form>

      {showVerificationForm ? (
        <form className="task-panel" onSubmit={verifyContact}>
          <h2>
            {deliveryIcon(deliveryChannel)} Verify your contact
          </h2>
          <p className="muted">{verificationPrompt(deliveryChannel)}</p>
          <label>
            <span>Verification code</span>
            <input
              inputMode="numeric"
              value={verificationCode}
              onChange={(event) => setVerificationCode(event.target.value)}
            />
          </label>
          <button type="submit" disabled={!verificationCode.trim()}>
            Verify contact
          </button>
        </form>
      ) : null}

      <section className="task-panel">
        <h2>
          <Map aria-hidden="true" size={20} /> Auto-process new activities
        </h2>
        <p className="muted">{autoProcessingCopy}</p>
        <p className="muted">
          <strong>Eligible means:</strong> the activity type is selected in Map settings, Strava has
          a route we can map safely, your setup is verified, and current processing limits have not
          been reached. <a href="/faq#eligibility">More in FAQ</a>.
        </p>
        <button
          type="button"
          disabled={!preferences.integration_enabled && !readiness.readyForManualTesting()}
          onClick={() => void setAutomaticProcessing(!preferences.integration_enabled)}
        >
          {preferences.integration_enabled ? "Turn off auto-processing" : "Turn on auto-processing"}
        </button>
      </section>
    </div>
  );
}

function ManualTestJourney({
  preferences,
  readiness,
  serviceStatus
}: {
  preferences: PreferencesResponse;
  readiness: SetupReadiness;
  serviceStatus: PublicStatusResponse;
}) {
  const [recentActivities, setRecentActivities] = useState<StravaActivitySummary[]>([]);
  const [processingAttempts, setProcessingAttempts] = useState<ProcessingAttemptSummary[]>([]);
  const [selectedActivityId, setSelectedActivityId] = useState("");
  const [manualActivityId, setManualActivityId] = useState("");
  const [activityTriggerStatus, setActivityTriggerStatus] = useState("");
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [attemptsLoading, setAttemptsLoading] = useState(false);

  useEffect(() => {
    if (preferences.strava_authenticated) {
      void loadRecentActivities();
      void loadProcessingAttempts();
    }
  }, [preferences.strava_authenticated]);

  async function loadRecentActivities() {
    setActivitiesLoading(true);
    try {
      const response = await api.listRecentActivities();
      setRecentActivities(response.activities);
      setSelectedActivityId((current) => current || response.activities[0]?.activity_id || "");
      setActivityTriggerStatus(response.activities.length ? "" : "No recent Strava activities found.");
      void track("recent_activities_loaded", { status: "success" });
    } catch {
      setRecentActivities([]);
      setActivityTriggerStatus("Could not load recent Strava activities.");
      void track("client_error", { page: "configuration", reason_code: "recent_activities_failed" });
    } finally {
      setActivitiesLoading(false);
    }
  }

  async function loadProcessingAttempts() {
    setAttemptsLoading(true);
    try {
      const response = await api.listProcessingAttempts();
      setProcessingAttempts(response.attempts);
    } catch {
      void track("client_error", { page: "configuration", reason_code: "attempt_history_failed" });
    } finally {
      setAttemptsLoading(false);
    }
  }

  async function triggerActivity(activityId: string, source: "recent" | "manual") {
    const trimmedActivityId = activityId.trim();
    if (!trimmedActivityId) {
      setActivityTriggerStatus("Enter a Strava activity ID first.");
      return;
    }
    if (!readiness.readyForManualTesting()) {
      setActivityTriggerStatus(readiness.blockers.map((blocker) => blocker.label).join(" "));
      return;
    }
    if (!serviceStatus.processing_available) {
      setActivityTriggerStatus("Map generation is temporarily paused. You do not need to retry this activity.");
      return;
    }
    setActivityTriggerStatus("Processing this activity...");
    try {
      const response = await api.triggerActivity(trimmedActivityId, source);
      setActivityTriggerStatus(resultMessage(response.status, response.reason_code));
      void loadProcessingAttempts();
      void track("manual_activity_triggered", { status: response.status });
    } catch {
      setActivityTriggerStatus("Could not process this activity.");
      void track("client_error", { page: "configuration", reason_code: "activity_trigger_failed" });
    }
  }

  return (
    <div className="journey-stack">
      <section className="task-panel">
        <h2>
          <Play aria-hidden="true" size={20} /> Test an activity
        </h2>
        <p className="muted">
          Pick a recent activity or paste a Strava activity ID or link. Tests can run even while
          auto-processing is off.
        </p>
        <div className="action-row">
          <label>
            <span>Choose a recent activity</span>
            <select
              value={selectedActivityId}
              disabled={!recentActivities.length}
              onChange={(event) => setSelectedActivityId(event.target.value)}
            >
              {!recentActivities.length ? (
                <option value="">
                  {activitiesLoading ? "Loading recent activities" : "No recent activities available"}
                </option>
              ) : null}
              {recentActivities.map((activity) => (
                <option key={activity.activity_id} value={activity.activity_id}>
                  {activityLabel(activity)}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="icon-action"
            disabled={activitiesLoading}
            aria-label="Refresh recent activities"
            title="Refresh recent activities"
            onClick={() => void loadRecentActivities()}
          >
            <RefreshCw aria-hidden="true" size={18} />
          </button>
        </div>
        <button
          type="button"
          disabled={!selectedActivityId || !readiness.readyForManualTesting() || !serviceStatus.processing_available}
          onClick={() => void triggerActivity(selectedActivityId, "recent")}
        >
          Process selected activity
        </button>
        <label>
          <span>Strava activity ID or link</span>
          <input
            inputMode="url"
            placeholder="https://www.strava.com/activities/14096332134"
            value={manualActivityId}
            onChange={(event) => setManualActivityId(event.target.value)}
          />
        </label>
        <button
          type="button"
          disabled={!manualActivityId.trim() || !readiness.readyForManualTesting() || !serviceStatus.processing_available}
          onClick={() => void triggerActivity(manualActivityId, "manual")}
        >
          Process this activity
        </button>
        {activityTriggerStatus ? (
          <p className="muted" role="status" aria-live="polite">
            {activityTriggerStatus}
          </p>
        ) : null}
      </section>
      <AttemptHistoryPanel
        attempts={processingAttempts}
        loading={attemptsLoading}
        serviceStatus={serviceStatus}
        onRefresh={() => void loadProcessingAttempts()}
      />
    </div>
  );
}

function AttemptHistoryPanel({
  attempts,
  loading,
  serviceStatus,
  onRefresh
}: {
  attempts: ProcessingAttemptSummary[];
  loading: boolean;
  serviceStatus: PublicStatusResponse;
  onRefresh(): void;
}) {
  const latestInterruption = attempts.find((attempt) => isServiceInterruptionReason(attempt.reason_code));
  return (
    <section className="task-panel">
      <div className="panel-heading-row">
        <h2>
          <RefreshCw aria-hidden="true" size={20} /> Recent processing
        </h2>
        <button
          type="button"
          className="icon-action"
          disabled={loading}
          aria-label="Refresh processing history"
          title="Refresh processing history"
          onClick={onRefresh}
        >
          <RefreshCw aria-hidden="true" size={18} />
        </button>
      </div>
      {latestInterruption ? (
        <p className="action-feedback warning" role="status">
          <span className="status-dot" /> {attemptHelpText(latestInterruption.reason_code)}{" "}
          <a href={supportMailto(serviceStatus.support_email, "Processing attempt skipped")}>
            Contact support
          </a>
        </p>
      ) : null}
      {attempts.length ? (
        <dl className="compact-list">
          {attempts.map((attempt) => (
            <div key={attempt.attempt_id}>
              <dt>
                <a href={stravaActivityUrl(attempt.activity_id)} target="_blank" rel="noreferrer">
                  Activity {attempt.activity_id}
                </a>
              </dt>
              <dd>
                {attemptResultMessage(attempt)} {attemptSourceLabel(attempt.source)}{" "}
                {formatAttemptTime(attempt.last_updated_at || attempt.completed_at || attempt.started_at)}
              </dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="muted">{loading ? "Loading processing history..." : "No activities have been processed yet."}</p>
      )}
    </section>
  );
}

function PreferencesJourney({ preferences, replacePreferences }: JourneyProps) {
  const [draft, setDraft] = useState<PreferenceUpdateRequest>(() => preferenceRequest(preferences));
  const [selectedSetId, setSelectedSetId] = useState(() => selectedPreferenceSetId(draft.preference_sets));
  const [activeActivityGroupLabel, setActiveActivityGroupLabel] = useState(activityTypeGroups[0].label);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [feedback, setFeedback] = useState<ActionFeedback | null>(null);

  useEffect(() => {
    const nextDraft = preferenceRequest(preferences);
    setDraft(nextDraft);
    setSelectedSetId((currentSetId) => selectedPreferenceSetId(nextDraft.preference_sets, currentSetId));
  }, [preferences]);

  const selectedSet =
    draft.preference_sets.find((preferenceSet) => preferenceSet.set_id === selectedSetId) ??
    draft.preference_sets[0] ??
    defaultPreferenceSet(preferences);
  const availableActivityGroups = activityGroupsForProvider(selectedSet.map_provider);
  const activeActivityGroup =
    availableActivityGroups.find((group) => group.label === activeActivityGroupLabel) ??
    availableActivityGroups[0];
  const activeActivityGroupSelectedCount = activeActivityGroup.options.filter((activityType) =>
    selectedSet.activity_types.includes(activityType)
  ).length;
  const selectedActivityLabels = selectedSet.activity_types
    .map((activityType) => activityTypeLabel(activityType))
    .sort((first, second) => first.localeCompare(second));
  const savedPreferenceWarnings = preferences.preference_sets.flatMap((preferenceSet) =>
    preferenceSet.warnings.map((warning) => ({
      key: `${preferenceSet.set_id}:${warning}`,
      message: `${preferenceSet.name}: ${warning}`
    }))
  );

  useEffect(() => {
    if (!availableActivityGroups.some((group) => group.label === activeActivityGroupLabel)) {
      setActiveActivityGroupLabel(availableActivityGroups[0].label);
    }
  }, [activeActivityGroupLabel, availableActivityGroups]);

  function requestWithPreferenceSets(
    current: PreferenceUpdateRequest,
    preferenceSets: PreferenceSetUpdateRequest[]
  ): PreferenceUpdateRequest {
    const normalizedSets = preferenceSets.some((preferenceSet) => preferenceSet.is_default)
      ? preferenceSets
      : preferenceSets.map((preferenceSet, index) => ({
          ...preferenceSet,
          is_default: index === 0
        }));
    const defaultSet = normalizedSets.find((preferenceSet) => preferenceSet.is_default) ?? normalizedSets[0];
    return {
      ...current,
      aspect_ratio: defaultSet.aspect_ratio,
      map_provider: defaultSet.map_provider,
      imagery_type: defaultSet.imagery_type,
      activity_types: defaultSet.activity_types,
      privacy_radius_meters: defaultSet.privacy_radius_meters,
      route_colour: defaultSet.route_colour,
      route_border_colour: defaultSet.route_border_colour,
      output_media_type: defaultSet.output_media_type,
      animation_marker_colour: defaultSet.animation_marker_colour,
      watermark_text: defaultSet.watermark_text,
      watermark_position: defaultSet.watermark_position,
      preference_sets: normalizedSets
    };
  }

  function updatePreferenceSets(updater: (sets: PreferenceSetUpdateRequest[]) => PreferenceSetUpdateRequest[]) {
    setDraft((current) => requestWithPreferenceSets(current, updater(current.preference_sets)));
  }

  function updateSelectedSet(changes: Partial<PreferenceSetUpdateRequest>) {
    if (changes.map_provider === "zwift") {
      const zwiftActivities = selectedSet.activity_types.filter((activityType) =>
        ["VirtualRide", "VirtualRun"].includes(activityType)
      );
      changes = {
        ...changes,
        imagery_type: "zwift_world",
        activity_types: zwiftActivities.length ? zwiftActivities : ["VirtualRide"]
      };
    } else if (changes.map_provider === "ordnance_survey") {
      const nonVirtualActivities = selectedSet.activity_types.filter(
        (activityType) => !["VirtualRide", "VirtualRun"].includes(activityType)
      );
      changes = {
        ...changes,
        imagery_type: selectedSet.imagery_type === "zwift_world" ? "default" : selectedSet.imagery_type,
        activity_types: nonVirtualActivities.length ? nonVirtualActivities : ["Run"]
      };
    }
    updatePreferenceSets((sets) =>
      sets.map((preferenceSet) =>
        preferenceSet.set_id === selectedSet.set_id ? { ...preferenceSet, ...changes } : preferenceSet
      )
    );
  }

  function addPreferenceSet() {
    if (draft.preference_sets.length >= 5) {
      setFeedback({ tone: "warning", message: "You can save up to five map preference sets." });
      return;
    }
    const setId = `set_${Date.now().toString(36)}`;
    const nextSet: PreferenceSetUpdateRequest = {
      ...selectedSet,
      set_id: setId,
      name: `Map ${draft.preference_sets.length + 1}`,
      enabled: true,
      is_default: false
    };
    updatePreferenceSets((sets) => [...sets, nextSet]);
    setSelectedSetId(setId);
  }

  function removeSelectedSet() {
    if (selectedSet.is_default || draft.preference_sets.length <= 1) {
      return;
    }
    const removedSetName = selectedSet.name.trim() || "This set";
    const nextSelectedSetId = selectedPreferenceSetId(
      draft.preference_sets.filter((preferenceSet) => preferenceSet.set_id !== selectedSet.set_id)
    );
    updatePreferenceSets((sets) => sets.filter((preferenceSet) => preferenceSet.set_id !== selectedSet.set_id));
    setSelectedSetId(nextSelectedSetId);
    setFeedback({
      tone: "warning",
      message: `${removedSetName} was removed from this draft. Save map settings to finish removing it.`
    });
  }

  function makeSelectedSetDefault() {
    updatePreferenceSets((sets) =>
      sets.map((preferenceSet) => ({
        ...preferenceSet,
        is_default: preferenceSet.set_id === selectedSet.set_id
      }))
    );
  }

  function toggleActivityType(activityType: ActivityType) {
    updatePreferenceSets((sets) => {
      const selected = new Set(selectedSet.activity_types);
      if (selected.has(activityType)) {
        selected.delete(activityType);
      } else {
        selected.add(activityType);
      }
      return sets.map((preferenceSet) =>
        preferenceSet.set_id === selectedSet.set_id
          ? { ...preferenceSet, activity_types: [...selected] }
          : preferenceSet
      );
    });
  }

  function toggleActivityGroup(activityTypes: ActivityType[]) {
    updatePreferenceSets((sets) => {
      const selected = new Set(selectedSet.activity_types);
      const allSelected = activityTypes.every((activityType) => selected.has(activityType));
      if (allSelected) {
        for (const activityType of activityTypes) {
          selected.delete(activityType);
        }
      } else {
        for (const activityType of activityTypes) {
          selected.add(activityType);
        }
      }
      return sets.map((preferenceSet) =>
        preferenceSet.set_id === selectedSet.set_id
          ? { ...preferenceSet, activity_types: [...selected] }
          : preferenceSet
      );
    });
  }

  async function savePreferences(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const missingActivitySet = draft.preference_sets.find(
      (preferenceSet) => preferenceSet.enabled && !preferenceSet.activity_types.length
    );
    if (missingActivitySet) {
      setFeedback({ tone: "warning", message: "Select at least one activity type before saving map settings." });
      return;
    }
    const payload = requestWithPreferenceSets(draft, draft.preference_sets);
    void track("preferences_save_started", {
      aspect_ratio: payload.aspect_ratio,
      map_provider: payload.map_provider,
      preference_set_count: payload.preference_sets.length
    });
    try {
      const response = await api.updatePreferences(payload);
      replacePreferences(response, response.warnings.length ? "Map settings saved with warnings" : "Map settings saved");
      setFeedback({
        tone: response.warnings.length ? "warning" : "success",
        message: response.warnings.length ? "Map settings saved with warnings." : "Map settings saved."
      });
      void track("preferences_save_succeeded", { status: "success" });
    } catch {
      setFeedback({ tone: "error", message: "Could not save map settings. Try again." });
      void track("preferences_save_failed", { reason_code: "api_error" });
    }
  }

  return (
    <form className="task-panel" onSubmit={savePreferences}>
      <h2>
        <SlidersHorizontal aria-hidden="true" size={20} /> Map settings
      </h2>
      <div className="preference-set-selector" role="tablist" aria-label="Map preference sets">
        {draft.preference_sets.map((preferenceSet) => (
          <button
            key={preferenceSet.set_id}
            type="button"
            className={preferenceSet.set_id === selectedSet.set_id ? "selected-set" : ""}
            role="tab"
            aria-selected={preferenceSet.set_id === selectedSet.set_id}
            onClick={() => setSelectedSetId(preferenceSet.set_id)}
          >
            {preferenceSet.name}
          </button>
        ))}
        <button type="button" className="secondary-action" onClick={addPreferenceSet}>
          Add set
        </button>
      </div>

      <div className="split-fields">
        <label>
          <span>Set name</span>
          <input
            maxLength={48}
            value={selectedSet.name}
            onChange={(event) => updateSelectedSet({ name: event.target.value })}
          />
        </label>
        <label className="check-row preference-set-check">
          <input
            type="checkbox"
            checked={selectedSet.enabled}
            onChange={(event) => updateSelectedSet({ enabled: event.target.checked })}
          />
          <span>Enabled</span>
        </label>
        <button
          type="button"
          className="secondary-action"
          disabled={selectedSet.is_default}
          onClick={makeSelectedSetDefault}
        >
          Make default
        </button>
        <button
          type="button"
          className="danger-action"
          disabled={selectedSet.is_default || draft.preference_sets.length <= 1}
          onClick={removeSelectedSet}
        >
          <Trash2 aria-hidden="true" size={16} />
          Remove set
        </button>
      </div>

      <div className="split-fields">
        <label>
          <span>Image shape</span>
          <select
            value={selectedSet.aspect_ratio}
            onChange={(event) => updateSelectedSet({ aspect_ratio: event.target.value as AspectRatio })}
          >
            <option value="default">Default</option>
            <option value="square">Square</option>
            <option value="16:9">16:9</option>
          </select>
        </label>
        <fieldset className="inline-radio-field">
          <legend>Output</legend>
          <div className="inline-radio-options">
            {[
              { value: "image" as OutputMediaType, label: "Image" },
              { value: "video" as OutputMediaType, label: "Animation" }
            ].map((option) => (
              <label
                key={option.value}
                className={
                  selectedSet.output_media_type === option.value
                    ? "inline-radio-option selected-radio-option"
                    : "inline-radio-option"
                }
              >
                <input
                  type="radio"
                  name={`output-media-${selectedSet.set_id}`}
                  value={option.value}
                  checked={selectedSet.output_media_type === option.value}
                  onChange={() => updateSelectedSet({ output_media_type: option.value })}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <label>
          <span>Map provider</span>
          <select
            value={selectedSet.map_provider}
            onChange={(event) => updateSelectedSet({ map_provider: event.target.value as MapProvider })}
          >
            <option value="ordnance_survey">Ordnance Survey</option>
            <option value="zwift">Zwift</option>
          </select>
        </label>
        <label>
          <span>Map style</span>
          <select
            value={selectedSet.imagery_type}
            onChange={(event) => updateSelectedSet({ imagery_type: event.target.value as MapImageryType })}
          >
            {imageryOptionsForProvider(selectedSet.map_provider).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      {selectedSet.map_provider === "zwift" ? (
        <p className="muted">Zwift maps are used only for Zwift virtual rides and runs on supported worlds.</p>
      ) : null}

      <fieldset>
        <legend>Activities to process automatically</legend>
        <p className="muted">
          Select the Strava activity types this map set should handle. New activities and manual
          tests only use map sets where the activity type is selected; unselected types are skipped
          for this set.
        </p>
        <div className="activity-picker">
          <div className="activity-family-tabs" role="tablist" aria-label="Activity groups">
            {availableActivityGroups.map((group) => {
              const selectedCount = group.options.filter((activityType) =>
                selectedSet.activity_types.includes(activityType)
              ).length;
              const active = group.label === activeActivityGroup.label;
              return (
                <button
                  key={group.label}
                  type="button"
                  className={active ? "selected-set" : ""}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveActivityGroupLabel(group.label)}
                >
                  <span>{group.label}</span>
                  <span className="activity-count">{selectedCount}</span>
                </button>
              );
            })}
          </div>

          <div
            className="activity-family-panel"
            role="tabpanel"
            aria-label={`${activeActivityGroup.label} activities`}
          >
            <div className="activity-panel-header">
              <div>
                <strong>{activeActivityGroup.label}</strong>
                <span className="muted">
                  {activeActivityGroupSelectedCount} of {activeActivityGroup.options.length}
                </span>
              </div>
              <div className="button-row compact-actions">
                <button
                  type="button"
                  className="secondary-action"
                  onClick={() => toggleActivityGroup(activeActivityGroup.options)}
                >
                  {activeActivityGroupSelectedCount === activeActivityGroup.options.length
                    ? "Clear group"
                    : "Select group"}
                </button>
              </div>
            </div>
            <div className="check-grid compact-check-grid">
              {activeActivityGroup.options.map((activityType) => (
                <label key={activityType} className="check-row">
                  <input
                    type="checkbox"
                    checked={selectedSet.activity_types.includes(activityType)}
                    onChange={() => toggleActivityType(activityType)}
                  />
                  <span>{activityTypeLabel(activityType)}</span>
                </label>
              ))}
            </div>
          </div>

          {selectedActivityLabels.length ? (
            <div className="selected-activity-summary-block">
              <p className="selected-activity-summary-label" id="selected-activity-types-label">
                Selected activity types
              </p>
              <div className="selected-activity-summary" aria-labelledby="selected-activity-types-label">
                {selectedActivityLabels.slice(0, 10).map((label) => (
                  <span key={label}>{label}</span>
                ))}
                {selectedActivityLabels.length > 10 ? (
                  <span>+{selectedActivityLabels.length - 10}</span>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
        {!selectedSet.activity_types.length ? (
          <p className="muted">Select at least one activity type before saving map settings.</p>
        ) : null}
      </fieldset>

      <button
        type="button"
        className="secondary-action disclosure-toggle"
        aria-controls="advanced-map-options"
        aria-expanded={advancedOpen}
        onClick={() => setAdvancedOpen((open) => !open)}
      >
        <ChevronDown aria-hidden="true" size={18} /> Advanced map options
      </button>
      {advancedOpen ? (
        <div className="advanced-grid" id="advanced-map-options">
          <label>
            <span>Route colour</span>
            <input
              type="color"
              value={selectedSet.route_colour}
              onChange={(event) => updateSelectedSet({ route_colour: event.target.value })}
            />
          </label>
          <label>
            <span>Route border</span>
            <input
              type="color"
              value={selectedSet.route_border_colour}
              onChange={(event) => updateSelectedSet({ route_border_colour: event.target.value })}
            />
          </label>
          <label>
            <span>Animation marker</span>
            <input
              type="color"
              value={selectedSet.animation_marker_colour}
              onChange={(event) => updateSelectedSet({ animation_marker_colour: event.target.value })}
            />
          </label>
          <label>
            <span>Privacy radius (m)</span>
            <input
              min="0"
              type="number"
              value={selectedSet.privacy_radius_meters}
              onChange={(event) => updateSelectedSet({ privacy_radius_meters: Number(event.target.value) })}
            />
          </label>
          <label>
            <span>Watermark</span>
            <input
              value={selectedSet.watermark_text}
              onChange={(event) => updateSelectedSet({ watermark_text: event.target.value })}
            />
          </label>
          <label>
            <span>Watermark position</span>
            <select
              value={selectedSet.watermark_position}
              onChange={(event) => updateSelectedSet({ watermark_position: event.target.value as WatermarkPosition })}
            >
              <option value="bottom_right">Bottom right</option>
              <option value="bottom_left">Bottom left</option>
              <option value="top_right">Top right</option>
              <option value="top_left">Top left</option>
            </select>
          </label>
        </div>
      ) : null}
      {savedPreferenceWarnings.map((warning) => (
        <p key={warning.key} className="muted">
          {warning.message}
        </p>
      ))}
      <button type="submit" disabled={!draft.preference_sets.some((preferenceSet) => preferenceSet.activity_types.length)}>
        Save map settings
      </button>
      <ActionFeedbackMessage feedback={feedback} />
    </form>
  );
}

function AccountManagementJourney({ preferences, replacePreferences, onUnlinked }: JourneyProps & { onUnlinked(): void }) {
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const [feedback, setFeedback] = useState<ActionFeedback | null>(null);

  async function revokeConsent() {
    if (!confirmRevoke) {
      setConfirmRevoke(true);
      return;
    }
    try {
      const response = await api.revokePrivacyDisclosure();
      replacePreferences(response, "Consent revoked. Delivery contact data was deleted and auto-processing is off.");
      setFeedback({ tone: "success", message: "Consent revoked and delivery contact data deleted." });
      setConfirmRevoke(false);
    } catch {
      setFeedback({ tone: "error", message: "Could not revoke consent. Try again." });
    }
  }

  async function deleteContact() {
    try {
      const response = await api.deleteContact();
      replacePreferences(response, "Delivery contact deleted. Add and verify a contact before processing again.");
      setFeedback({ tone: "success", message: "Delivery contact deleted." });
    } catch {
      setFeedback({ tone: "error", message: "Could not delete delivery contact. Try again." });
    }
  }

  async function unlinkAccount() {
    if (!confirmUnlink) {
      setConfirmUnlink(true);
      return;
    }
    try {
      await api.unlink();
      onUnlinked();
    } catch {
      setFeedback({ tone: "error", message: "Could not unlink Strava. Try again." });
    }
  }

  return (
    <div className="journey-stack">
      <ActionFeedbackMessage feedback={feedback} />

      <section className="task-panel danger">
        <h2>
          <UserRoundX aria-hidden="true" size={20} /> Consent and delivery data
        </h2>
        <p>Revoking consent deletes your delivery contact and stops all processing until you complete setup again.</p>
        <p className="muted">
          <a href="/privacy-security" target="_blank" rel="noreferrer">
            Review the Privacy and Security Notice
          </a>
        </p>
        <div className="button-row">
          <button type="button" className="secondary-action" disabled={!preferences.masked_destination} onClick={() => void deleteContact()}>
            Delete delivery contact
          </button>
          <button type="button" className="danger-action" onClick={() => void revokeConsent()}>
            {confirmRevoke ? "Yes, revoke consent" : "Revoke consent"}
          </button>
        </div>
      </section>

      <section className="task-panel danger">
        <h2>
          <UserRoundX aria-hidden="true" size={20} /> Unlink Strava
        </h2>
        <p>Unlinking removes your Strava connection and deletes saved delivery contact details.</p>
        <p>
          Feedback, bug reports, and other messages you have sent us separately are not deleted by
          unlinking Strava.
        </p>
        <button type="button" className="danger-action" onClick={() => void unlinkAccount()}>
          {confirmUnlink ? "Yes, unlink Strava" : "Unlink Strava"}
        </button>
      </section>
    </div>
  );
}

function HelpJourney({ serviceStatus }: { serviceStatus: PublicStatusResponse }) {
  useEffect(() => {
    void track("help_opened", { page: "configuration" });
  }, []);

  return (
    <div className="journey-stack">
      <section className="task-panel">
        <h2>
          <CircleHelp aria-hidden="true" size={20} /> Help and FAQ
        </h2>
        <p className="muted">
          Common setup, privacy, processing, and delivery questions are available in the public FAQ.
        </p>
        <div className="button-row">
          <a className="button-link secondary-action" href="/faq">
            Open FAQ
          </a>
          <a className="button-link" href={supportMailto(serviceStatus.support_email, "Custom activity maps help", supportRequestBody())}>
            <Mail aria-hidden="true" size={18} /> Contact support
          </a>
        </div>
      </section>
      <FaqContent supportEmail={serviceStatus.support_email} compact />
    </div>
  );
}

function PrivacySecurityPolicyPage() {
  return (
    <main className="app-shell policy-shell">
      <header className="topbar">
        <div>
          <h1>Privacy and Security Notice</h1>
          <p>Custom activity maps</p>
        </div>
        <a className="button-link secondary-action" href="/">
          Back to setup
        </a>
      </header>

      <article className="policy-document">
        <section>
          <p>
            Custom activity maps creates route-map images from your Strava activities and sends
            them to the delivery address you choose. This notice explains what data is used, why it
            is used, how it is protected, and the third-party terms that also apply.
          </p>
        </section>

        <section>
          <h2>Who is responsible</h2>
          <p>
            Custom activity maps is operated by Callipygian Code Ltd, a company registered in
            England and Wales with company number 09755366.
          </p>
        </section>

        <section>
          <h2>Age and permission</h2>
          <p>
            This service is not intended for children. You should use it only if you are 18 or
            older, or if you have permission from your parent or legal guardian.
          </p>
        </section>

        <section>
          <h2>What you agree to</h2>
          <p>By enabling this integration, you agree that Custom activity maps may:</p>
          <ul>
            <li>Connect to your Strava account using Strava OAuth.</li>
            <li>
              Read your recent and new Strava activities, including route/location data, activity
              type, activity ID, visibility information, and source or device attribution where
              available.
            </li>
            <li>
              Generate a map image from eligible activities using Ordnance Survey map imagery or,
              where enabled, Zwift world imagery for supported Zwift virtual rides and runs.
            </li>
            <li>Apply your route privacy settings before generating the image.</li>
            <li>
              Send generated images to your verified email address, Telegram chat, or WhatsApp
              number.
            </li>
            <li>
              Store the minimum data needed to operate the integration, including encrypted Strava
              tokens, encrypted delivery contact details, preferences, consent records, processing
              status, delivery status, and short-lived operational logs.
            </li>
          </ul>
        </section>

        <section>
          <h2>What we do not do</h2>
          <p>
            We do not use your Strava data, route data, generated images, contact details, or
            delivery information for advertising, targeted advertising, model training, data
            brokerage, or unrelated analytics.
          </p>
        </section>

        <section>
          <h2>Feedback and support messages</h2>
          <p>
            If you send us feedback, bug reports, support requests, or other inbound communications
            by email, Telegram, WhatsApp, or any other channel, we handle those messages separately
            from your Strava integration data. These messages may include the contact details,
            account identifiers, screenshots, activity references, technical details, and other
            information you choose to send.
          </p>
          <p>
            We retain these communications separately so we can investigate issues, improve the
            service, understand user needs, and maintain development records. Unlinking Strava,
            revoking consent in the app, deleting your delivery contact, or changing delivery
            channel does not delete this feedback, bug-report, or support-message data.
          </p>
          <p>
            To delete inbound communications you have sent us, request deletion separately through
            the same channel or another support channel available to you. We may need enough
            information to locate the relevant messages before deleting them.
          </p>
        </section>

        <section>
          <h2>Route privacy warning</h2>
          <p>
            Strava privacy settings may not always be available to this app as exact hidden
            start/end distances. We apply the route-trimming setting you configure before creating
            an image, but a generated map may still reveal sensitive location information,
            especially around repeated routes, unusual locations, or distinctive route shapes. Only
            enable the integration if you are comfortable receiving generated map images by your
            chosen delivery channel.
          </p>
        </section>

        <section>
          <h2>Map imagery restrictions</h2>
          <p>
            Generated images may include Ordnance Survey map imagery and required attribution. You
            must not use generated images to scrape, extract, bulk download, digitise, recreate, or
            redistribute the underlying map data. Where Zwift imagery is enabled, generated images
            may include Zwift map artwork and required attribution. Generated images are for your
            personal activity-map use only.
          </p>
        </section>

        <section>
          <h2>Delivery</h2>
          <p>
            If you choose email, we use Amazon SES to send verification emails and generated map
            images.
          </p>
          <p>
            If you choose Telegram, we use the Telegram Bot API to send verification messages and
            generated map images to the chat you link. Telegram may process delivery and
            usage data under its own terms.
          </p>
          <p>
            If WhatsApp delivery is enabled and you choose it, you consent to us sending generated
            activity-map messages to your WhatsApp number. WhatsApp and Meta may process delivery
            and usage data under their own terms. You can withdraw delivery consent by deleting
            your contact details, changing delivery channel, revoking consent, or unlinking your
            account.
          </p>
        </section>

        <section>
          <h2>Retention and deletion</h2>
          <p>
            Generated images are not intentionally kept after delivery beyond what is needed for
            delivery and retry/status handling.
          </p>
          <p>
            When you unlink your account or revoke consent, we delete or disable the data needed to
            continue processing, including Strava credentials, contact details, preferences, and
            user-owned processing records. Some short-lived operational records, logs, queue
            messages, or delivery-status records may remain briefly until automatic expiry or
            managed-service retention completes.
          </p>
          <p>
            Feedback, bug reports, support requests, and other inbound communications are retained
            separately from Strava integration data. These are not deleted when you unlink Strava or
            revoke app consent; deletion must be requested separately through the communication or
            support channels available to you.
          </p>
          <p>You can also revoke this app's access from your Strava account settings.</p>
        </section>

        <section>
          <h2>Your rights</h2>
          <p>
            If UK data protection law applies to you, you may have rights to access, correct,
            delete, restrict, object to, or receive a portable copy of your personal data. You can
            use the in-app unlink, contact deletion, and consent revocation controls for common
            requests, or contact Callipygian Code Ltd through the support route you used to get
            access to the service.
          </p>
          <p>
            You can complain to the Information Commissioner's Office if you are unhappy with how
            your data is handled.
          </p>
        </section>

        <section>
          <h2>Where data is processed</h2>
          <p>
            The application is hosted primarily in the UK AWS region. Some providers used by the
            service, including Strava, Telegram, WhatsApp, Buttondown, Slack, email providers, and
            global AWS services such as CloudFront, may process data outside the UK under their own
            terms and transfer safeguards.
          </p>
        </section>

        <section>
          <h2>Security</h2>
          <p>
            We use various security controls to protect your data, including signed browser sessions,
            server-side map tile fetching, encrypted storage for Strava tokens and contact details,
            rate limits, callback signature checks, short log retention, and telemetry controls
            designed to avoid sensitive data.
          </p>
          <p>
            No online service is risk-free. If you believe your account, delivery address, or
            generated image has been exposed incorrectly, contact the operator who gave you access
            to this private beta.
          </p>
        </section>

        <section>
          <h2>External terms</h2>
          <p>This app depends on third-party services. Your use is also affected by:</p>
          <ul className="link-list">
            <li>
              <a href="https://www.strava.com/legal/api" target="_blank" rel="noreferrer">
                Strava API Agreement
              </a>
            </li>
            <li>
              <a href="https://www.strava.com/legal/terms" target="_blank" rel="noreferrer">
                Strava Terms of Service
              </a>
            </li>
            <li>
              <a
                href="https://support.strava.com/hc/en-us/articles/360001487844-Data-and-Privacy"
                target="_blank"
                rel="noreferrer"
              >
                Strava Data and Privacy
              </a>
            </li>
            <li>
              <a
                href="https://docs.os.uk/more-than-maps/support/legal/legal-or-support-or-os-data-hub"
                target="_blank"
                rel="noreferrer"
              >
                OS Data Hub legal terms page
              </a>
            </li>
            <li>
              <a href="https://osdatahub.os.uk/legal/termsConditions" target="_blank" rel="noreferrer">
                OS Data Hub terms
              </a>
            </li>
            <li>
              <a href="https://aws.amazon.com/service-terms/" target="_blank" rel="noreferrer">
                AWS Service Terms
              </a>
            </li>
            <li>
              <a href="https://aws.amazon.com/aup/" target="_blank" rel="noreferrer">
                AWS Acceptable Use Policy
              </a>
            </li>
            <li>
              <a href="https://telegram.org/tos" target="_blank" rel="noreferrer">
                Telegram Terms of Service
              </a>
            </li>
            <li>
              <a href="https://www.whatsapp.com/legal/business-terms" target="_blank" rel="noreferrer">
                WhatsApp Business Terms
              </a>
            </li>
            <li>
              <a
                href="https://www.whatsapp.com/legal/business-solution-terms"
                target="_blank"
                rel="noreferrer"
              >
                WhatsApp Business Solution Terms
              </a>
            </li>
            <li>
              <a
                href="https://www.whatsapp.com/legal/business-data-processing-terms"
                target="_blank"
                rel="noreferrer"
              >
                WhatsApp Business Data Processing Terms
              </a>
            </li>
          </ul>
        </section>

        <section className="consent-copy">
          <h2>Consent statement</h2>
          <p>
            I have read and agree to the Privacy and Security Notice. I understand that Custom
            activity maps will access my Strava activity data, process route/location data to
            generate map images, use Ordnance Survey map imagery or supported Zwift imagery where
            enabled, and send generated images to my chosen verified delivery contact.
          </p>
          <p>
            I confirm that I am 18 or older, or that I have permission from my parent or legal
            guardian to use this service.
          </p>
          <p>
            I understand that feedback, bug reports, support requests, and other messages I send by
            email, Telegram, WhatsApp, or another channel are handled and retained separately to
            support development and service improvement, and are not deleted by unlinking Strava.
          </p>
          <p>
            If I choose Telegram delivery, I agree to receive generated activity-map messages via
            Telegram in the chat I link. I understand I can withdraw this consent by deleting my
            contact details, changing delivery channel, revoking consent, or unlinking my account.
          </p>
          <p>
            If I choose WhatsApp delivery, I agree to receive generated activity-map messages via
            WhatsApp at the number I provide. I understand I can withdraw this consent by deleting
            my contact details, changing delivery channel, revoking consent, or unlinking my
            account.
          </p>
        </section>
      </article>
    </main>
  );
}

function FaqPage() {
  const [serviceStatus, setServiceStatus] = useState<PublicStatusResponse>(fallbackServiceStatus);

  useEffect(() => {
    void track("faq_viewed", { page: "faq" });
    async function loadStatus() {
      try {
        setServiceStatus(publicStatusWithDefaults(await api.getPublicStatus()));
      } catch {
        setServiceStatus(statusUnavailableFallback());
        void track("client_error", { page: "faq", reason_code: "public_status_failed" });
      }
    }
    void loadStatus();
  }, []);

  return (
    <main className="app-shell policy-shell">
      <header className="topbar">
        <div>
          <h1>FAQ</h1>
          <p>Custom activity maps</p>
        </div>
        <div className="button-row">
          <a className="button-link secondary-action" href="/">
            Setup
          </a>
          <a className="button-link secondary-action" href="/privacy-security">
            Privacy and security
          </a>
        </div>
      </header>

      <ServiceNoticeBanner
        notice={serviceStatus.service_notices.public}
        supportEmail={serviceStatus.support_email}
      />

      <FaqContent supportEmail={serviceStatus.support_email} />
    </main>
  );
}

function FaqContent({ supportEmail, compact = false }: { supportEmail: string; compact?: boolean }) {
  return (
    <article className={compact ? "faq-document compact" : "faq-document"}>
      <section>
        <p className="eyebrow">Getting started</p>
        <h2>What does this app do?</h2>
        <p>
          It creates route-map images and route animations from eligible Strava activities
          using your saved map, output, privacy, and delivery settings. You can receive maps
          automatically for new activities or run a manual test from a recent activity.
        </p>
      </section>

      <section>
        <p className="eyebrow">Setup</p>
        <h2>What do I need to complete before maps or animations are sent?</h2>
        <p>
          Connect Strava, accept the privacy, map, and delivery notice, add a delivery contact,
          verify that contact, choose image or animation output for your map preference sets, then
          turn on auto-processing if you want new activities handled automatically.
        </p>
      </section>

      <section>
        <p className="eyebrow">Testing</p>
        <h2>Can I test one activity before turning on automatic processing?</h2>
        <p>
          Yes. Once Strava is connected and your delivery contact is verified, use Test activity to
          pick a recent activity or paste a Strava activity link. Manual tests use your matching
          map preference sets and work even while auto-processing is off.
        </p>
      </section>

      <section id="eligibility">
        <p className="eyebrow">Eligibility</p>
        <h2>Why did an activity not create a map?</h2>
        <p>
          The usual reasons are: the activity type is not selected in Map settings, the route cannot
          be mapped safely, the delivery contact is not verified, daily limits have been reached, or
          map generation is temporarily paused. Zwift maps only apply to supported Zwift virtual
          rides and runs, and animation output may be skipped if the route is too short or animation
          generation is temporarily unavailable.
        </p>
      </section>

      <section>
        <p className="eyebrow">Zwift</p>
        <h2>Can it make maps for Zwift activities?</h2>
        <p>
          Yes, where supported. Add a map preference set that uses the Zwift map provider and select
          virtual ride or virtual run activity types. Zwift routes are rendered over supported Zwift
          world imagery rather than real-world Ordnance Survey mapping.
        </p>
      </section>

      <section>
        <p className="eyebrow">Animations</p>
        <h2>Can I receive an animated route map?</h2>
        <p>
          Yes. In Map settings, choose Animation as the output for a preference set. Animations are
          delivered as video files, use a moving marker to show route progress, and have their own
          daily limit because they take more processing time than still images.
        </p>
      </section>

      <section>
        <p className="eyebrow">Delivery</p>
        <h2>Where are maps and animations sent?</h2>
        <p>
          Maps and animations are sent only to the delivery contact you save and verify. Email and
          Telegram can deliver images and animations. WhatsApp may be available for image
          delivery where it has been enabled for the service; video delivery is not currently
          configured for WhatsApp.
        </p>
      </section>

      <section>
        <p className="eyebrow">Strava</p>
        <h2>Why is the image or animation not added to my Strava activity automatically?</h2>
        <p>
          Some integrations, such as indoor-training platforms, can make activity images appear in
          Strava, so it is reasonable to expect this might be possible. Those integrations may use
          Strava-approved partner capabilities or activity-upload flows that are not the same as a
          general public API for attaching a generated image to an existing activity.
        </p>
        <p>
          This app uses Strava's documented public API surface. Strava documents APIs for creating
          or uploading activity data and for updating some activity fields, but attaching a
          generated image or animation to an existing activity is not a documented public workflow
          this app can safely rely on. For now, the app sends the finished media to your verified
          delivery channel so you stay in control of whether and where to share it.
        </p>
      </section>

      <section>
        <p className="eyebrow">Privacy</p>
        <h2>How is my start or end location protected?</h2>
        <p>
          The app applies your configured privacy radius before rendering a map image. That reduces
          exposure around route endpoints, but route shapes can still reveal sensitive places, so
          choose a radius that fits how and where you move.
        </p>
      </section>

      <section>
        <p className="eyebrow">Data</p>
        <h2>Are route data and generated images or animations stored?</h2>
        <p>
          Route details and generated image or animation bytes are intended to be transient during
          generation and delivery. The app keeps short-lived processing and delivery status records
          so you can see what happened and so failures can be investigated safely.
        </p>
      </section>

      <section>
        <p className="eyebrow">Service status</p>
        <h2>What should I do if map or animation generation is paused?</h2>
        <p>
          You do not need to resend the same activity. Setup, settings, and account controls remain
          available; new image or animation generation will resume when the operator has cleared
          the cost, provider, reliability, or safety issue.
        </p>
      </section>

      <section>
        <p className="eyebrow">Support</p>
        <h2>How do I get help?</h2>
        <p>
          Check Recent processing in Test activity first; it shows the last five attempts and the
          current status. If you contact support, include the approximate time, delivery channel,
          and visible status or reason code. Do not send route files, Strava tokens, raw queue
          messages, or screenshots that reveal sensitive locations unless the operator specifically
          asks for a safer way to share them.
        </p>
        <a className="button-link" href={supportMailto(supportEmail, "Custom activity maps help", supportRequestBody())}>
          <Mail aria-hidden="true" size={18} /> Contact support
        </a>
      </section>
    </article>
  );
}

export function resultMessage(status: string, reasonCode: string) {
  const readableReasons: Record<string, string> = {
    started: "Processing started.",
    manual_trigger_started: "Manual test started.",
    queued: "Queued. Your map will be created soon.",
    retrying: "Trying again after a temporary problem.",
    delivered: "Map image delivered.",
    partial_delivery: "Some matching map options were delivered, but others did not complete.",
    all_variants_failed: "No map was delivered because every matching map option failed.",
    processing_paused: "Map generation is temporarily paused. No action is needed.",
    global_daily_generation_quota_exhausted: "Map generation is temporarily capped for today.",
    global_monthly_generation_quota_exhausted: "Map generation is temporarily capped this month.",
    global_daily_tile_quota_exhausted: "Map generation is temporarily capped for today.",
    global_monthly_tile_quota_exhausted: "Map generation is temporarily capped this month.",
    queue_enqueue_failed: "Processing could not be started. Please try again shortly.",
    queue_not_configured: "Processing queue is not configured. Please try again later.",
    credential_missing: "Reconnect Strava before trying again.",
    decryption_failed: "Secure account data could not be read. Please try again later.",
    encrypted_payload_invalid: "Secure account data could not be read. Please try again later.",
    encryption_method_unsupported: "Secure account data could not be read. Please try again later.",
    integration_not_enabled: "Turn on the integration before processing activities automatically.",
    processing_prerequisites_missing: "Accept the latest terms before processing.",
    contact_not_verified: "Verify your delivery contact before processing.",
    account_not_processable: "This Strava account cannot process activities right now.",
    event_not_create: "Only newly created Strava activities are processed automatically.",
    activity_owner_mismatch: "This activity does not belong to the linked Strava account.",
    activity_not_eligible: "This activity type is not set up for map generation.",
    activity_deleted: "Activity was deleted in Strava, so no map was created.",
    all_variants_skipped: "No map was created because every matching map option was skipped for this activity.",
    daily_quota_exhausted: "Daily image limit reached.",
    daily_image_quota_exhausted: "Daily image limit reached.",
    daily_animation_quota_exhausted: "Daily animation limit reached.",
    route_not_mappable: "This route cannot be mapped safely.",
    route_not_renderable: "This route is too short or too limited to generate a useful map.",
    tile_count_exceeded: "This activity is too large for the current map settings.",
    memory_limit_exceeded: "This route would need more image memory than the service allows.",
    output_size_exceeded: "This map would be too large to deliver with the current settings.",
    image_generation_timeout: "Map generation took too long. Please try again later.",
    map_tile_unavailable: "Map tiles are temporarily unavailable. Please try again later.",
    ordnance_survey_tile_http_error: "Ordnance Survey map tiles could not be loaded. Please try again later.",
    ordnance_survey_tile_unavailable: "Ordnance Survey map tiles are temporarily unavailable. Please try again later.",
    zwift_assets_disabled: "Zwift map generation is not enabled for this world.",
    ambiguous_zwift_world: "This Zwift route could not be matched to a single world confidently.",
    unsupported_zwift_world: "This Zwift route is not supported for map generation yet.",
    zwift_tile_unavailable: "Zwift map tiles are unavailable for this route.",
    animation_generation_unavailable: "Animation generation is not configured yet. Please try again later.",
    animation_generation_timeout: "Animation generation took too long. Please try again later.",
    animation_route_too_short: "This route is too short to animate.",
    animation_encoder_unavailable: "Animation encoding is not configured yet. Please try again later.",
    animation_encoder_integrity_failed: "Animation encoding failed a safety check. Please try again later.",
    animation_encoder_incompatible: "Animation encoding is incompatible with the current runtime. Please try again later.",
    animation_encoding_failed: "Animation encoding failed. Please try again later.",
    strava_authorization_revoked: "Reconnect Strava before trying again.",
    strava_not_configured: "Strava integration is not configured. Please try again later.",
    strava_rate_limited: "Strava rate limit reached. Please try again later.",
    strava_unavailable: "Strava is temporarily unavailable. Please try again later.",
    strava_activity_not_found: "Strava could not find this activity.",
    strava_activity_fetch_failed: "Strava activity details could not be fetched. Please try again later.",
    strava_stream_fetch_failed: "Strava activity stream data could not be fetched. Please try again later.",
    strava_activity_list_failed: "Recent Strava activities could not be fetched. Please try again later.",
    strava_invalid_polyline: "Strava route data could not be read.",
    strava_invalid_response: "Strava returned an unexpected response. Please try again later.",
    strava_profile_fetch_failed: "Strava profile details could not be fetched. Please try again later.",
    strava_token_exchange_failed: "Strava authorization could not be completed. Please try again.",
    strava_token_refresh_failed: "Strava authorization could not be refreshed. Reconnect Strava before trying again.",
    delivery_channel_unavailable: "Delivery is not configured for the selected channel.",
    delivery_channel_mismatch: "Delivery settings do not match the verified contact.",
    ses_message_too_large: "This map is too large to send by email.",
    telegram_delivery_failed: "Telegram delivery failed. Please try again later.",
    telegram_invalid_response: "Telegram returned an unexpected response. Please try again later.",
    telegram_unavailable: "Telegram delivery is not configured right now.",
    whatsapp_unavailable: "WhatsApp delivery is not configured right now.",
    whatsapp_video_delivery_unsupported: "WhatsApp video delivery is not configured right now."
  };
  if (readableReasons[reasonCode]) {
    return readableReasons[reasonCode];
  }
  if (reasonCode.startsWith("ses_")) {
    return "Email delivery failed. Please try again later.";
  }
  if (reasonCode.startsWith("whatsapp_")) {
    return "WhatsApp delivery failed. Please try again later.";
  }
  if (reasonCode.startsWith("telegram_")) {
    return "Telegram delivery failed. Please try again later.";
  }
  if (reasonCode.startsWith("strava_")) {
    return "Strava could not complete the request. Please try again later.";
  }
  if (reasonCode.startsWith("unexpected_")) {
    return "Something went wrong while processing this activity. Please try again later.";
  }
  return `${statusLabel(status)}: ${humanizeReasonCode(reasonCode)}.`;
}

export function attemptResultMessage(attempt: ProcessingAttemptSummary) {
  if (
    attempt.reason_code === "all_variants_skipped" &&
    attempt.variant_outcomes.length > 0 &&
    attempt.variant_outcomes.every((outcome) => outcome.reason_code === "tile_count_exceeded")
  ) {
    return "No map was created because this activity is too large for the current map settings.";
  }
  return resultMessage(attempt.status, attempt.reason_code);
}

function statusLabel(status: string) {
  const labels: Record<string, string> = {
    dead_lettered: "Processing stopped",
    delivered: "Delivered",
    failed: "Failed",
    generated: "Generated",
    queued: "Queued",
    retrying: "Retrying",
    skipped: "Skipped",
    started: "Processing"
  };
  return labels[status] ?? humanizeReasonCode(status);
}

function humanizeReasonCode(reasonCode: string) {
  return reasonCode.replace(/_/g, " ").trim() || "unknown status";
}

function isServiceInterruptionReason(reasonCode: string) {
  return (
    reasonCode === "processing_paused" ||
    reasonCode === "global_daily_generation_quota_exhausted" ||
    reasonCode === "global_monthly_generation_quota_exhausted" ||
    reasonCode === "global_daily_tile_quota_exhausted" ||
    reasonCode === "global_monthly_tile_quota_exhausted"
  );
}

function attemptHelpText(reasonCode: string) {
  if (reasonCode === "processing_paused") {
    return "This attempt was skipped because map generation is paused. You do not need to resend it.";
  }
  return "This attempt was skipped by a service-wide safety cap. Try again later rather than repeating the same request.";
}

function supportRequestBody() {
  return [
    "Approximate time:",
    "Delivery channel:",
    "Visible status or reason code:",
    "",
    "Please do not include route files, Strava tokens, raw queue messages, or screenshots that reveal sensitive locations."
  ].join("\n");
}

function supportMailto(emailAddress: string, subject: string, body?: string) {
  const params = new URLSearchParams({ subject });
  if (body) {
    params.set("body", body);
  }
  return `mailto:${emailAddress.trim() || "maps@example.com"}?${params.toString()}`;
}

function formatAttemptTime(value: string | null) {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) {
    return "";
  }
  return date.toLocaleString();
}

function attemptSourceLabel(source: string) {
  if (source === "manual") {
    return "Manual ID test.";
  }
  if (source === "recent") {
    return "Recent activity test.";
  }
  return "";
}
